import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from 'react-router-dom';
import { Capacitor } from '@capacitor/core';
import { IconButton } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import SendIcon from "@mui/icons-material/Send";
import axiosInstance from "../api/axiosInstance";
import { formatDistanceToNow } from "date-fns";
import { fromZonedTime, toZonedTime } from "date-fns-tz";
import { Dialog } from "@capacitor/dialog";
import ReportDialog from "../components/ReportDialog";
import { CircularProgress } from '@mui/material';
import "../styles/comments.css";
import { useAuth } from '../context/AuthContext'; // adjust path if needed

const FlashComments = ({ flashId, onClose }) => {
  const navigate = useNavigate();

  const longPressTimer = useRef(null);
  const startPosition = useRef({ x: 0, y: 0 });
  const hasMoved = useRef(false);

  const isIos = Capacitor.getPlatform() === 'ios';
  const [isClosing, setIsClosing] = useState(false);
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState("");
  const [replyTo, setReplyTo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [isReportDialogOpen, setIsReportDialogOpen] = useState(false);
  const [reportEntityId, setReportEntityId] = useState(null);
  const [reportEntityType, setReportEntityType] = useState("flash_comment");
  const { userId } = useAuth();

  const openReportDialog = (type, entityId) => {
    setReportEntityType(type);
    setReportEntityId(entityId);
    setIsReportDialogOpen(true);
  };

  const closeReportDialog = () => {
    setIsReportDialogOpen(false);
    setReportEntityId(null);
    setReportEntityType("flash_comment");
  };

  const fetchComments = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get(`/flashes/comments/${flashId}`);
      setComments(response.data.comments);
    } catch (err) {
      console.error("Error fetching comments:", err);
    } finally {
      setLoading(false);
    }
  };

  const fetchRepliesForComment = async (commentId, offset = 0, limit = 4) => {
    try {
      const response = await axiosInstance.get(
        `/flashes/comments/${commentId}/replies`,
        {
          params: { offset, limit },
        }
      );
      return response.data;
    } catch (err) {
      console.error("Error fetching replies:", err);
      return { replies: [], totalReplies: 0 };
    }
  };

  const postCommentOrReply = async () => {
    try {
      if (replyTo) {
        await axiosInstance.post("/flashes/replies", {
          comment_id: replyTo.comment_id,
          reply_text: newComment,
        });
      } else {
        await axiosInstance.post("/flashes/comments", {
          flash_id: flashId,
          comment_text: newComment,
        });
      }
      setNewComment("");
      setReplyTo(null);
      fetchComments();
    } catch (err) {
      console.error("Error posting comment or reply:", err);
    }
  };

  const handleReply = (comment) => {
    setReplyTo(comment);
    setNewComment(`@${comment.username} `);
  };

  const resetReply = () => {
    setReplyTo(null);
    setNewComment("");
  };

  const toggleReplies = async (comment, idx) => {
    // If replies are not shown, load the first batch
    if (!comment.showReplies) {
      const { replies, totalReplies } = await fetchRepliesForComment(
        comment.comment_id,
        0,   // offset
        4    // limit
      );
  
      setComments((prevComments) => {
        const newComments = [...prevComments];
        newComments[idx] = {
          ...comment,
          showReplies: true,
          replies,             // the fetched replies
          totalReplies,        // total number of replies in DB
          loadedRepliesCount: replies.length, // how many we've loaded so far
        };
        return newComments;
      });
    }
    else {
      // If replies are already shown, hide them
      setComments((prevComments) => {
        const newComments = [...prevComments];
        newComments[idx] = {
          ...comment,
          showReplies: false,
        };
        return newComments;
      });
    }
  };

  const loadMoreReplies = async (comment, idx) => {
    const newOffset = comment.loadedRepliesCount || 0; // how many are already loaded
  
    const { replies: newReplies } = await fetchRepliesForComment(
      comment.comment_id,
      newOffset,
      4
    );
  
    setComments((prevComments) => {
      const newComments = [...prevComments];
      const existingReplies = comment.replies || [];
  
      newComments[idx] = {
        ...comment,
        replies: [...existingReplies, ...newReplies], // append new replies
        loadedRepliesCount: newOffset + newReplies.length,
      };
      return newComments;
    });
  };

  const handleLongPress = async (item, type, commentIdx, replyIdx = null) => {
   
    if (item.user_id !== userId) {
      if (type === "comment") {
        openReportDialog("comment", item.comment_id);
      }
      else if (type === "reply") {
        openReportDialog("reply", item.reply_id);
      }
    }
    else {
      if (replyIdx !== null) {
        const { value } = await Dialog.confirm({
          title: "Delete Reply",
          message: "Are you sure you want to delete this reply?",
        });
  
        if (value) {
          try {
            await axiosInstance.delete(`/trippins/replies/${item.reply_id}`);
            setComments((prev) => {
              const updated = [...prev];
              const currentReplies = updated[commentIdx].replies || [];
              updated[commentIdx].replies = currentReplies.filter((_, i) => i !== replyIdx);
              return updated;
            });
          } catch (err) {
            console.error("Error deleting reply:", err);
          }
        }
        return;
      }
  
      const { value } = await Dialog.confirm({
        title: "Delete Comment",
        message: "Are you sure you want to delete this comment?",
      });
  
      if (value) {
        try {
          await axiosInstance.delete(`/trippins/comments/${item.comment_id}`);
          setComments((prev) => prev.filter((_, i) => i !== commentIdx));
        } catch (err) {
          console.error("Error deleting comment:", err);
        }
      }
    }
  };

  const handleMouseDown = (item, type, commentIdx, replyIdx = null, event) => {
    event.stopPropagation();
    
    const clientX = event.clientX || event.touches?.[0]?.clientX;
    const clientY = event.clientY || event.touches?.[0]?.clientY;
  
    startPosition.current = { x: clientX, y: clientY };
    hasMoved.current = false;
  
    longPressTimer.current = setTimeout(() => {
      if (!hasMoved.current) {
        handleLongPress(item, type, commentIdx, replyIdx);
      }
    }, 800);
  };
  
  const handleMouseMove = (event) => {
    if (!longPressTimer.current) return;
  
    const clientX = event.clientX || event.touches?.[0]?.clientX;
    const clientY = event.clientY || event.touches?.[0]?.clientY;
  
    const deltaX = Math.abs(clientX - startPosition.current.x);
    const deltaY = Math.abs(clientY - startPosition.current.y);
  
    if (deltaX > 1 || deltaY > 1) {
      hasMoved.current = true;
      clearTimeout(longPressTimer.current);
      longPressTimer.current = null;
    }
  };
  
  const handleMouseUp = () => {
    clearTimeout(longPressTimer.current);
    longPressTimer.current = null;
  };

  const getTimeAgo = (utcTimestamp) => {
    const utcDate = fromZonedTime(new Date(utcTimestamp), "UTC");
    const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const localTime = toZonedTime(utcDate, userTimeZone);

    return formatDistanceToNow(localTime, { addSuffix: true });
  };

  useEffect(() => {
    if (flashId) {
      fetchComments();
    }
  }, [flashId]);

  const handleClose = () => {
    setIsClosing(true);
    setTimeout(() => {
      onClose();
    }, 300);
  };

  return (
    <div className="comments-overlay" onClick={handleClose}>
      <div
        className={`comments-container ${isClosing ? "close" : "open"}`}
        onClick={(e) => e.stopPropagation()}
      >
        <ReportDialog
          open={isReportDialogOpen}
          onClose={closeReportDialog}
          reportedEntityId={reportEntityId}
          entityType={reportEntityType}
        />
        <div className="comments-header">
          <h3 className="comments-title">Comments</h3>
          <IconButton className="icon-button" onClick={handleClose}>
            <CloseIcon />
          </IconButton>
        </div>

        {/* Comments List */}
        <div className="comments-list">
          {loading && (
            <div className="loading-container-comments">
              <CircularProgress size={40} sx={{ color: '#d1d5db' }} />
            </div>
          )}
          {!loading && comments.length === 0 && (
            <div className="loading-container" style={{ height: "unset" }}>
              <p style={{ fontSize: "18px", color: "#555", textAlign: "center", marginTop: "50px" }}>
                No comments yet. Be the first to comment
              </p>
            </div>
          )}
          {comments.map((comment, idx) => (
            <div 
              key={comment.comment_id}
              className="comment-container"
              onMouseDown={(e) => handleMouseDown(comment, "comment", idx, null, e)}
              onMouseUp={() => handleMouseUp(comment)}
              onMouseMove={handleMouseMove}
              onTouchStart={(e) => handleMouseDown(comment, "comment", idx, null, e)}
              onTouchEnd={() => handleMouseUp(comment)}
              onTouchMove={handleMouseMove}
            >
              <div 
                className="comment-header"
                onClick={() => navigate(`/profile/${comment.user_id}`)}
              >
                <img
                  src={comment.profile_url || "/assets/images/default_profile.png"}
                  alt={comment.username}
                  className="comment-profile-pic"
                />
                <span className="comment-username">{comment.username}</span>
                <span className="comment-time">{getTimeAgo(comment.created_at)}</span>
              </div>

              <div className="comment-text">{comment.comment_text}</div>

              <div className="comment-actions">
                <span onClick={() => handleReply(comment)} className="reply-button">Reply</span>
              </div>

              {/* Replies */}
              {comment.reply_count > 0 && (
                <div onClick={() => toggleReplies(comment, idx)} className="replies-toggle-button">
                  {comment.showReplies ? (
                    <strong>Hide</strong>
                  ) : (
                    comment.reply_count > 1 ? `— View (${comment.reply_count}) replies` : `— View (${comment.reply_count}) reply`
                  )}
                </div>
              )}

              {comment.showReplies && comment.replies && (
                <div className="replies-section">
                  {comment.replies.map((reply, replyIdx) => (
                    <div
                      key={reply.reply_id}
                      className="reply-container"
                      onMouseDown={(e) => handleMouseDown(reply, "reply", idx, replyIdx, e)}
                      onMouseUp={(e) => { handleMouseUp(reply); e.stopPropagation(); }}
                      onMouseMove={handleMouseMove}
                      onTouchStart={(e) => handleMouseDown(reply, "reply", idx, replyIdx, e)}
                      onTouchEnd={(e) => { handleMouseUp(reply); e.stopPropagation(); }}
                      onTouchMove={handleMouseMove}
                    >
                      <div
                        className="comment-header"
                        onClick={() => navigate(`/profile/${reply.user_id}`)}
                      >
                        <img
                          src={reply.profile_url || "/assets/images/default_profile.png"}
                          alt={reply.username}
                          className="comment-profile-pic"
                        />
                        <span className="comment-username">{reply.username}</span>
                        <span className="comment-time">{getTimeAgo(reply.created_at)}</span>
                      </div>

                      <div className="comment-actions">
                        <div className="comment-text">{reply.reply_text}</div>
                      </div>
                    </div>
                  ))}

                  {comment.showReplies && comment.loadedRepliesCount < comment.totalReplies && (
                    <div
                      onClick={() => loadMoreReplies(comment, idx)}
                      className="replies-toggle-button"
                      style={{ fontSize: "14px", fontWeight: "bold" }}
                    >
                      - View more ({comment.totalReplies - comment.loadedRepliesCount}) replies
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>

        {replyTo && (
          <div className="reply-to-container">
            <span className="reply-to-text">Reply to: @{replyTo.username}</span>
            <IconButton onClick={resetReply} size="small" className="close-reply-button">
              <CloseIcon />
            </IconButton>
          </div>
        )}
        <div
          className="comment-input-section"
          style={isIos ? { marginBottom: '20px' } : {}}
        >          
          <input
            type="text"
            placeholder="Add comment..."
            value={newComment}
            onChange={(e) => setNewComment(e.target.value)}
            className="comment-input"
          />
          <button
            onClick={postCommentOrReply}
            className="post-comment-button"
            disabled={!newComment.trim()}
          >
            <SendIcon sx={{ width: 20, height: 20 }} />
          </button>
        </div>
      </div>
    </div>
  );
};

export default FlashComments;